import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, Row, Input, Space } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import CheckoutFormSidebar from 'components/cart/CheckoutFormSidebar';
import WishlistButton from 'components/cart/WishlistButton';
import {
	fetchCartProducts,
	removeFromCart,
	addToCart,
	reduceFromCart,
} from 'store/slices/cartSlice';
import { setSigninModalIsOpen } from 'store/slices/layoutSlice';
import useCustomer from 'hooks/useCustomer';
import { loadingStatus } from 'helpers/fetcher';
import { formatCurrency, formatWeightWithUnit, displayMoney } from 'helpers/formatter';
import useSmartNavigate from 'hooks/useSmartNavigate';
import {
	getStock,
	getMaxAvailableInCartUnit,
	isWeightPricedProduct,
} from 'helpers/product';
import Icon from 'components/Icon';

const { TextArea } = Input;

const Cart = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { data, status } = useSelector(state => state.cart.cartProducts);
	const { customer } = useCustomer();
	const { navigate } = useSmartNavigate();
	const [comment, setComment] = useState('');
	const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
	const [isCommentFocused, setIsCommentFocused] = useState(false);
	const [showCheckoutForm, setShowCheckoutForm] = useState(false);
	const sortDropdownRef = useRef(null);

	const handleRemoveFromCart = async cart_id => {
		await removeFromCart(cart_id);
		dispatch(fetchCartProducts({ refetchAfterMutation: true }));
	};

	const handleAddToCart = async (product_id, quantity = 1) => {
		const cartItem = data.products?.find(
			p => String(p.product_id) === String(product_id)
		);
		await addToCart(
			product_id,
			quantity,
			customer.token ? undefined : (cartItem || { product_id })
		);
		dispatch(fetchCartProducts({ refetchAfterMutation: true }));
	};

	const handleReduceFromCart = async product_id => {
		await reduceFromCart(product_id, 1);
		dispatch(fetchCartProducts({ refetchAfterMutation: true }));
	};

	useEffect(() => {
		dispatch(fetchCartProducts());
	}, [dispatch]);

	if (
		status === loadingStatus.SUCCEEDED &&
		(!data.products || data.products.length === 0)
	) {
		return (
			<div className='region cart-section'>
				<div className='contact-breadcrumb'>
					<a
						href='/'
						onClick={e => {
							e.preventDefault();
							navigate('/');
						}}
						className='breadcrumb-link'
					>
						{t('common.home')}
					</a>
					<span className='breadcrumb-separator breadcrumb-separator-active'></span>
					<span className='breadcrumb-current'>{t('cart.breadcrumb')}</span>
				</div>
				<div className='cart-header'>
					<h1 className='cart-title'>{t('cart.title')}</h1>
				</div>
				<div className='cart-empty'>
					<p className='cart-empty-text'>{t('cart.emptyCart')}</p>
					<Button 
						type='primary' 
						size='large'
						className='cart-empty-button'
						onClick={() => navigate('/catalog')}
					>
						{t('cart.goToCatalog')}
					</Button>
				</div>
			</div>
		);
	}

	const inStockCount =
		data.products?.filter(item => {
			const stock = getStock({
				quantity:
					item.stock_qty !== undefined && item.stock_qty !== null
						? Number(item.stock_qty)
						: 999,
				weight: item.weight,
				unit: item.unit,
				main_unit: item.main_unit,
				pre_order: item.pre_order,
			});
			return stock.quantity > 0 || stock.stock > 0;
		}).length || 0;

	const extractNumericValue = value => {
		if (!value && value !== 0) return 0;
		if (typeof value === 'number') return value;
		const cleaned = `${value}`
			.replaceAll(' ', '')
			.replaceAll('₽', '')
			.replaceAll(',', '.');
		const num = parseFloat(cleaned);
		return isNaN(num) ? 0 : num;
	};

	const total =
		data.products?.reduce((sum, item) => {
			const itemTotal = extractNumericValue(item.total);
			return sum + itemTotal;
		}, 0) ||
		(data.totals?.find(t => t.code === 'total')?.value
			? extractNumericValue(data.totals.find(t => t.code === 'total').value)
			: 0);
	const discount =
		data.totals?.find(t => t.code === 'total' && t.title?.includes('скидк'))
			?.value || 0;

	return (
		<div className='region cart-section'>
			<div className='contact-breadcrumb'>
				<a
					href='/'
					onClick={e => {
						e.preventDefault();
						navigate('/');
					}}
					className='breadcrumb-link'
				>
				{t('common.home')}
			</a>
			<span className='breadcrumb-separator breadcrumb-separator-active'></span>
			<span className='breadcrumb-current'>{t('cart.breadcrumb')}</span>
		</div>
			<Row gutter={[24, 24]} style={{ alignItems: 'flex-start' }}>
				<Col xs={24} md={16}>
					<div className='cart-header'>
					<h1 className='cart-title'>{t('cart.title')}</h1>
					<div className='category-sort-wrapper' ref={sortDropdownRef}>
						<button
							type='button'
							className={`category-sort-button ${
								isSortDropdownOpen ? 'active' : ''
							}`}
							onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
						>
							<span>{t('cart.sortByAdded')}</span>
							<Icon
								name='icon-chevron-down'
								className={`category-sort-icon ${isSortDropdownOpen ? 'rotated' : ''}`}
								ariaLabel={t('header.sort')}
							/>
						</button>
						{isSortDropdownOpen && (
							<div className='category-sort-dropdown-menu'>
								<div
									className='category-sort-dropdown-item active'
									onClick={() => setIsSortDropdownOpen(false)}
								>
									{t('cart.sortByAdded')}
								</div>
								<div
									className='category-sort-dropdown-item'
									onClick={() => setIsSortDropdownOpen(false)}
								>
									{t('cart.sortByPrice')}
								</div>
								<div
									className='category-sort-dropdown-item'
									onClick={() => setIsSortDropdownOpen(false)}
								>
									{t('cart.sortByName')}
								</div>
							</div>
						)}
						</div>
					</div>
				{inStockCount > 0 && (
					<p className='cart-in-stock'>
						{t('cart.inStock')}{' '}
						<span className='cart-in-stock-count'>
							{inStockCount}{' '}
							{inStockCount === 1
								? t('cart.itemSingular')
								: inStockCount < 5
								? t('cart.itemFew')
								: t('cart.itemMany')}
						</span>
					</p>
				)}
					<div className='cart-items'>
						{data.products?.map((item, index) => {
							const inCartQty = Number(item.quantity) || 0;
							const maxAvailable = getMaxAvailableInCartUnit(item);
							const step = Number(item.minimum) || 1;
							const isPlusDisabled = inCartQty + step > maxAvailable;
							const weightPriced = isWeightPricedProduct(item);
							const unitPriceDisplay = weightPriced
								? item.price_kg || item.price
								: item.price;
							const totalPrice = item.total || 0;

							const handleGotoItem = () => {
								if (item.product_id) {
									const slug =
										item.slug || item.name?.replace(/\s+/g, '-') || 'product';
									navigate(`/product/${item.product_id}/${slug}`);
								}
							};

							return (
								<div key={item.cart_id || index} className='cart-item'>
									<div className='cart-item-block-1'>
										<div
											className='cart-item-image'
											onClick={e => {
												e.stopPropagation();
												handleGotoItem();
											}}
											style={{ cursor: 'pointer' }}
										>
											<img src={item.thumb} alt={item.name} />
										</div>
										<div
											className='cart-item-info'
											onClick={e => {
												e.stopPropagation();
												handleGotoItem();
											}}
											style={{ cursor: 'pointer' }}
										>
											<h3 className='cart-item-title'>
												{item.name}, {formatWeightWithUnit(item.weight)}
											</h3>
										<p className='cart-item-price-per-kg'>
											{weightPriced ? (
												<>
													{t('cart.unitPricePerKg')}{' '}
													{displayMoney(unitPriceDisplay)}
												</>
											) : item.weight ? (
												t('cart.packWeightInfo', {
													weight: formatWeightWithUnit(item.weight),
												})
											) : (
												<>
													{t('cart.unitPricePerPiece')}{' '}
													{displayMoney(unitPriceDisplay)}
												</>
											)}
										</p>
										</div>
									</div>

									<div
										className='cart-item-block-2'
										onClick={e => e.stopPropagation()}
									>
										<div className='cart-item-quantity'>
											<Space.Compact block>
												<Button
													onClick={e => {
														e.stopPropagation();
														handleReduceFromCart(item.product_id);
													}}
													type='primary'
													icon={<MinusOutlined />}
												/>
												<Input
													value={`${item.quantity || 1} ${
														item.main_unit || 'кг'
													}`}
													className='cart-quantity-input'
													readOnly
												/>
												<Button
													type='primary'
													icon={<PlusOutlined />}
													disabled={isPlusDisabled}
													onClick={e => {
														e.stopPropagation();
														handleAddToCart(item.product_id, item.minimum || 1);
													}}
												/>
											</Space.Compact>
										</div>
									</div>

									<div
										className='cart-item-block-3'
										onClick={e => e.stopPropagation()}
									>
										<span className='cart-item-total-price'>
											{formatCurrency(totalPrice)}
										</span>
									</div>

									<div
										className='cart-item-block-4'
										onClick={e => e.stopPropagation()}
									>
										<WishlistButton
											product_id={item.product_id}
											active={item.in_wishlist}
											useSvgIcon={true}
											size={20}
											iconClassName='cart-item-wishlist'
										/>
										<button
											className='cart-item-remove'
											onClick={e => {
												e.stopPropagation();
												handleRemoveFromCart(item.cart_id);
											}}
										>
											<Icon name='icon-trash-cart' ariaLabel={t('cart.remove')} />
										</button>
									</div>
								</div>
							);
						})}
					</div>
				</Col>
				<Col xs={24} md={7} style={{ marginLeft: 'auto' }}>
					<div className='cart-sidebar'>
						{!showCheckoutForm ? (
							<>
								<Button
									type='primary'
									size='large'
									block
									className='cart-checkout-button'
									onClick={() => {
										if (!customer.token) {
											dispatch(setSigninModalIsOpen(true));
											return;
										}
										setShowCheckoutForm(true);
									}}
								>
									<span>{t('cart.toCheckout')}</span>
									<Icon name='icon-arrow-right-circle' className='cart-checkout-button-icon' />
								</Button>

								<div className='cart-summary-card'>
									<div className='cart-summary-total'>
									<span className='cart-summary-label'>{t('cart.total')}</span>
										<span className='cart-summary-total-price'>
											{formatCurrency(total)}
										</span>
									</div>

								<div className='cart-summary-row'>
									<span className='cart-summary-label'>{t('cart.assembly')}</span>
									<span className='cart-summary-value'>{t('cart.free')}</span>
								</div>

									{discount > 0 && (
										<div className='cart-summary-row'>
											<span className='cart-summary-label'>{t('cart.discount')}</span>
											<span className='cart-summary-value cart-summary-discount'>
												-{formatCurrency(discount)}
											</span>
										</div>
									)}

									<div className='cart-summary-comment contact-form-field contact-form-field-textarea'>
									<TextArea
										placeholder={t('cart.commentPlaceholder')}
											value={comment}
											onChange={e => setComment(e.target.value)}
											onFocus={() => setIsCommentFocused(true)}
											onBlur={() => setIsCommentFocused(false)}
											maxLength={100}
											rows={3}
											className='cart-comment-textarea contact-form-textarea'
										/>
									<label
										className={`contact-form-label ${
											isCommentFocused || comment
												? 'contact-form-label-active'
												: ''
										}`}
									>
										{t('cart.comment')}
									</label>
										<span className='cart-comment-counter contact-form-counter'>
											{comment.length}/100
										</span>
									</div>
								</div>
							</>
						) : (
							<CheckoutFormSidebar
								customer={customer}
								data={data}
								onBack={() => setShowCheckoutForm(false)}
							/>
						)}
					</div>
				</Col>
			</Row>
		</div>
	);
};

export default Cart;
